import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Card, Col, Row, Spin } from "antd";
import { observer } from "mobx-react";
import { useEffect, useMemo, useState } from "react";
import { UpsellModal } from "@devowl-wp/react-cookie-banner-admin";
import { DashboardCacheCardContent } from "./cache.js";
import { DashboardChecklistCardContent } from "./checklist.js";
import { DashboardGeneralCardContent } from "./general.js";
import { DashboardHintCardContent } from "./hint.js";
import { DashboardMigrationCardContent } from "./migration.js";
import { DashboardServiceCloudCardContent } from "./serviceCloud.js";
import { useStores } from "../../../store/stores.js";
import { __ } from "../../../utils/i18n.js";
import { DateRangePicker } from "../../dateRangePicker.js";
import { StatsMainBarChart } from "../consent/stats/mainBar.js";
const HREF_STATS = "#/consent";
const DashboardCards = observer(()=>{
    const { statsStore, optionStore, cookieStore, checklistStore } = useStores();
    const { others: { isPro, assetsUrl, hints: { dashboardTile } }, dashboardMigration } = optionStore;
    const { filters: { dates } } = statsStore;
    const [statsLiteInContainerRef, setStatsLiteInContainerRef] = useState();
    // Load initial content for all the dashboard cards
    useEffect(()=>{
        // checklistStore.fetchChecklist(); // this is done in `header.tsx`
        cookieStore.groups.get();
        optionStore.fetchCurrentRevision();
    }, []);
    // Make the other cards `opacity` until a given amount of checklist items got resolved
    const otherCardsOpacity = checklistStore.completed.length < 3 || dashboardMigration ? 0.5 : 1;
    const generalCard = useMemo(()=>/*#__PURE__*/ _jsx(Card, {
            style: {
                margin: 10,
                opacity: otherCardsOpacity
            },
            title: __("General"),
            className: "rcb-dashboard-card-focus",
            children: /*#__PURE__*/ _jsx(DashboardGeneralCardContent, {})
        }), [
        otherCardsOpacity
    ]);
    const migrationCard = useMemo(()=>dashboardMigration ? /*#__PURE__*/ _jsx(Card, {
            style: {
                margin: 10
            },
            title: dashboardMigration.headline,
            extra: /*#__PURE__*/ _jsx("a", {
                href: "#",
                onClick: ()=>optionStore.dismissMigration(),
                children: __("Dismiss this info")
            }),
            children: /*#__PURE__*/ _jsx(DashboardMigrationCardContent, {})
        }) : null, [
        dashboardMigration,
        optionStore
    ]);
    const checklistCard = useMemo(()=>/*#__PURE__*/ _jsx(Card, {
            style: {
                margin: 10,
                opacity: dashboardMigration ? otherCardsOpacity : undefined
            },
            title: __("Set up the cookie banner"),
            extra: checklistStore.done ? null : /*#__PURE__*/ _jsx("a", {
                href: "#",
                onClick: ()=>checklistStore.toggleChecklistItem("all", true),
                children: __("I have already done all the steps")
            }),
            children: /*#__PURE__*/ _jsx(DashboardChecklistCardContent, {})
        }), [
        checklistStore.done,
        dashboardMigration
    ]);
    const hintCards = useMemo(()=>dashboardTile.map(({ title, ...rest })=>/*#__PURE__*/ _jsx(Card, {
                style: {
                    margin: 10,
                    opacity: otherCardsOpacity
                },
                title: title,
                className: "rcb-dashboard-card-focus",
                children: /*#__PURE__*/ _jsx(DashboardHintCardContent, {
                    ...rest
                })
            }, title)), [
        dashboardTile,
        otherCardsOpacity
    ]);
    return checklistStore.items.length === 0 ? // Show a spinner until all content is loaded (avoid flickering due to checklist cards movement)
    /*#__PURE__*/ _jsx(Spin, {
        spinning: true,
        style: {
            marginTop: 15
        }
    }) : /*#__PURE__*/ _jsx(Spin, {
        spinning: checklistStore.busyChecklist,
        style: {
            marginTop: 15
        },
        children: /*#__PURE__*/ _jsxs(Row, {
            children: [
                /*#__PURE__*/ _jsxs(Col, {
                    xl: 16,
                    sm: 16,
                    xs: 24,
                    children: [
                        migrationCard,
                        checklistStore.done ? generalCard : checklistCard,
                        /*#__PURE__*/ _jsx(Card, {
                            style: {
                                margin: 10,
                                opacity: otherCardsOpacity
                            },
                            className: "rcb-dashboard-card-focus",
                            title: __("Statistics"),
                            extra: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx("a", {
                                        href: HREF_STATS,
                                        children: __("More statistics")
                                    }),
                                    "\xa0\xa0•\xa0\xa0",
                                    /*#__PURE__*/ _jsx(DateRangePicker, {
                                        value: /* Ant design relies on moment 2.27 and typings are currently incompatible with other moment typings */ dates,
                                        disabled: true
                                    })
                                ]
                            }),
                            children: isPro ? /*#__PURE__*/ _jsx(StatsMainBarChart, {}) : /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(UpsellModal, {
                                        title: __("Want to see detailed statistics about the consents of your visitors?"),
                                        inContainer: true,
                                        inContainerElement: statsLiteInContainerRef,
                                        testDrive: true,
                                        feature: "dashboard-stats",
                                        description: __("You can get several statistics about how your users use the cookie banner. This helps you to calculate the total number of users who do not want to be tracked, for example, by extrapolating data from Google Analytics."),
                                        showHints: false,
                                        showFomoCouponCounter: checklistStore.done
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        ref: setStatsLiteInContainerRef,
                                        className: "rcb-antd-modal-mount",
                                        style: {
                                            height: 600,
                                            backgroundImage: `url('${assetsUrl}dashboard-statistics-blured.png')`
                                        }
                                    })
                                ]
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(Col, {
                    xl: 8,
                    sm: 8,
                    xs: 24,
                    children: [
                        checklistStore.done ? checklistCard : generalCard,
                        hintCards,
                        /*#__PURE__*/ _jsx(Card, {
                            style: {
                                margin: 10,
                                opacity: otherCardsOpacity
                            },
                            title: __("Service Cloud"),
                            className: "rcb-dashboard-card-focus",
                            children: /*#__PURE__*/ _jsx(DashboardServiceCloudCardContent, {})
                        }),
                        /*#__PURE__*/ _jsx(Card, {
                            style: {
                                margin: 10,
                                opacity: otherCardsOpacity
                            },
                            title: __("Cache"),
                            className: "rcb-dashboard-card-focus",
                            children: /*#__PURE__*/ _jsx(DashboardCacheCardContent, {})
                        })
                    ]
                })
            ]
        })
    });
});
export { DashboardCards };
